import React, { useState, useEffect } from "react";
import { 
	message, 
	Button, 
	Drawer, 
	Space, 
	Input, 
	Upload, 
	Table, 
	Form
} from "antd";

import PropTypes from "prop-types";
import { admitedEntities } from "./utils";
import { DeleteOutlined, DownloadOutlined, ExclamationCircleOutlined, QuestionCircleOutlined, UploadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { Tooltip } from "antd";

const EditableCellPropTypes = {
	title: PropTypes.string,
	editable: PropTypes.bool,
	children: PropTypes.node,
	dataIndex: PropTypes.string,
	record: PropTypes.object,
	handleSave: PropTypes.func,
};

export const ImportEntitiesDrawer = ({	
	company_id, 
	// entities, 
	openAddClient, 
	onOpenAddClient, 
	importData,
	entitySelected,
}) => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	// const [count, setCount] = useState(0);
	const [form] = Form.useForm();

	const handleUpload = ({ file }) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			const workbook = XLSX.read(e.target.result, { type: "binary" });
			const sheetNombre = workbook.SheetNames[0];
			const sheet = workbook.Sheets[sheetNombre];
			const json = XLSX.utils.sheet_to_json(sheet);
			setData(json.map((item, index) => ({ ...item, key: index })));
			// setCount(json.length);
		};
		reader.readAsBinaryString(file);
	};

	const handleDelete = (key) => {
		const newData = data.filter((item) => item.key !== key);
		setData(newData);
	};

	const handleSave = (row) => {
		const newData = [...data];
		const index = newData.findIndex((item) => row.key === item.key);
		const item = newData[index];
		newData.splice(index, 1, { ...item, ...row });
		setData(newData);
	};

	const EditableCell = ({
		title,
		editable,
		children,
		dataIndex,
		record,
		handleSave,
		...restProps
	}) => {
		const [editing, setEditing] = useState(false);
		const inputRef = React.useRef(null);

		useEffect(() => {
			if (editing) {
				inputRef.current.focus();
			}
		}, [editing]);

		const toggleEdit = () => {
			setEditing(!editing);
			form.setFieldsValue({ [dataIndex]: record[dataIndex] });
		};

		const save = async () => {
			try {
				const values = await form.validateFields();
				toggleEdit();
				handleSave({ ...record, ...values });
			} catch (errInfo) {
				console.log("Save failed:", errInfo);
			}
		};

		let childNode = children;

		if (editable) {
			childNode = editing ? (
				<Form.Item
					style={{ margin: 0 }}
					name={dataIndex}
					rules={[
						{
							required: true,
							message: `${title} es requerido.`,
						},
					]}
				>
					<Input ref={inputRef} onPressEnter={save} onBlur={save} />
				</Form.Item>
			) : (
				<div
					className="editable-cell-value-wrap"
					style={{ paddingRight: 24 }}
					onClick={toggleEdit}
				>
					{children}
				</div>
			);
		}

		return <td {...restProps}>{childNode}</td>;
	};

	EditableCell.propTypes = EditableCellPropTypes;

	const hasErrors = data.some(item => item.error);

	const columns = [
		{
			title: (
				<span>
					NIT
					<Tooltip title="Obligatorio.">
						<ExclamationCircleOutlined style={{ marginLeft: 4 }} />
					</Tooltip>
				</span>
			),
			dataIndex: "NIT",
			key: "NIT",
			editable: true,
			width: 120,
		},
		{
			title: (
				<span>
					Nombre
					<Tooltip title="No es obligatorio colocar el nombre si el NIT es correcto. ARO reconocerá el nombre automáticamente.">
						<QuestionCircleOutlined style={{ marginLeft: 4 }} />
					</Tooltip>
				</span>
			),
			dataIndex: "Nombre",
			key: "Nombre",
			editable: true,
			width: 150,
		},
		{
			title: (
				<span>
					Nombre del Contacto
					<Tooltip title="Obligatorio.">
						<ExclamationCircleOutlined style={{ marginLeft: 4 }} />
					</Tooltip>
				</span>
			),
			dataIndex: "NombreContacto",
			key: "NombreContacto",
			editable: true,
			width: 150,
		},
		{
			title: (
				<span>
					Telefono del Contacto
					<Tooltip title="No es obligatorio colocar el número de teléfono del contacto, también se puede colocar '0'.">
						<QuestionCircleOutlined style={{ marginLeft: 4 }} />
					</Tooltip>
				</span>
			),
			dataIndex: "TelefonoContacto",
			key: "TelefonoContacto",
			editable: true,
			width: 150,
		},
		{
			title: (
				<span>
					Correo del Contacto
					<Tooltip title="Obligatorio.">
						<ExclamationCircleOutlined style={{ marginLeft: 4 }} />
					</Tooltip>
				</span>
			),
			dataIndex: "CorreoContacto",
			key: "CorreoContacto",
			editable: true,
			width: 200,
			ellipsis: true,
			render: (text) => (
				<div style={{ 
					maxWidth: 200,
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap"
				}} title={text}>
					{text}
				</div>
			),
		},
		...(hasErrors ? [{
			title: "Mensaje",
			dataIndex: "error",
			key: "error",
			width: 200,
			render: (text) => (
				text ? <span style={{ color: "red" }}>{text}</span> : null
			),
		}] : []),
		{
			title: "Acción",
			dataIndex: "accion",
			width: 80,
			render: (_, record) =>
				data.length >= 1 ? (
					<Button 
						type="text" 
						icon={<DeleteOutlined />} 
						// eslint-disable-next-line react/prop-types
						onClick={() => handleDelete(record.key)}
						style={{ color: "red" }}
						loading={loading}
					/>
				) : null,
		},
	];

	const mergedColumns = columns.map((col) => {
		if (!col.editable) {
			return col;
		}
		return {
			...col,
			onCell: (record) => ({
				record,
				editable: col.editable,
				dataIndex: col.dataIndex,
				title: col.title,
				handleSave: handleSave,
			}),
		};
	});

	const handleImport = async () => {
		if (data.length === 0) {
			message.error("No hay datos para importar");
			return;
		}

		setLoading(true);

		const notification = {
			company_id: company_id,
			entities: data.map(item => ({
				name: item.Nombre || "",
				tid: String(item.NIT || ""),
				contact: {
					name: item.NombreContacto || "",
					phone: item.TelefonoContacto || "0",
					email: item.CorreoContacto || "",
				},
			})),
		};
		
		const result = await importData(notification);
		if (result) {
			message.success(`Se importaron ${result.success.length} ${admitedEntities[entitySelected].plural}`);
			
			setData(prevData => {
				// First, process and mark errors
				let updatedData = prevData.map(item => {
					if (result.errors && result.errors.length > 0) {
						const error = result.errors.find(err => String(item.NIT) === String(err.tid));
						if (error) {
							return { ...item, error: error.message };
						}
					}
					return item;
				});
				
				// Then filter out successful records
				if (result.success && result.success.length > 0) {
					const successTids = result.success.map(item => String(item.tid));
					updatedData = updatedData.filter(item => !successTids.includes(String(item.NIT)));
				}
				
				return updatedData;
			});
			
		} else {
			message.error(`Error al importar ${admitedEntities[entitySelected].plural}`);
		}

		setLoading(false);
		// setCount(result.success ? data.length - result.success.length : data.length);
	};

	const downloadExample = () => {
		const exampleData = [
			{ 
				NIT: "900123456", 
				Nombre: "Empresa Ejemplo S.A.", 
				NombreContacto: "Juan Pérez", 
				TelefonoContacto: "20202020", 
				CorreoContacto: "juan.perez@ejemplo.com" 
			},
			{ 
				NIT: "800987654", 
				Nombre: "Compañía Modelo Ltda.", 
				NombreContacto: "María Rodríguez", 
				TelefonoContacto: "0", 
				CorreoContacto: "maria.rodriguez@modelo.com" 
			}
		];
		
		const worksheet = XLSX.utils.json_to_sheet(exampleData);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Plantilla");
		
		XLSX.writeFile(workbook, `Plantilla_${admitedEntities[entitySelected].plural}.xlsx`);
	};

	return (
		<Drawer
			title={`Importar ${admitedEntities[entitySelected].plural}`}
			placement="right"
			width={"100vw"}
			onClose={() => onOpenAddClient(false)}
			open={openAddClient}
			extra={
				<Space>
					<Button 
						type="primary" 
						onClick={downloadExample}
						icon={<DownloadOutlined />}
						loading={loading}
						ghost
					>
						Descargar Ejemplo
					</Button>
					<Upload 
						beforeUpload={() => false} 
						onChange={handleUpload}
						showUploadList={false}
					>
						<Button 
							type="primary" 
							ghost
							icon={<UploadOutlined />}
							loading={loading}
						>
							Subir Plantilla
						</Button>
					</Upload>
					<Button 
						type="primary" 
						disabled={data.length === 0}
						onClick={handleImport}
						loading={loading}
					>
						Importar
					</Button>
				</Space>
			}
		>
			<Form form={form} component={false}>
				<Table 
					components={{
						body: {
							cell: EditableCell,
						},
					}}
					dataSource={data} 
					columns={mergedColumns} 
					rowClassName="editable-row"
					pagination={false}
					bordered
				/>
			</Form>
		</Drawer>
	);
};

ImportEntitiesDrawer.propTypes = {	
	company_id: PropTypes.string.isRequired,
	// entities: PropTypes.array.isRequired,
	importData: PropTypes.func.isRequired,
	openAddClient: PropTypes.bool.isRequired,
	onOpenAddClient: PropTypes.func.isRequired,
	addNewClient: PropTypes.func.isRequired,
	entitySelected: PropTypes.string.isRequired,
};
